import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { platformClient } from "@/lib/musicApi";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";

// First-time tooltip tracking (onboarding.md Step 3). Loads the hints this
// subscriber has already seen once, and records new ones. Shown once ever.
const HintsContext = createContext();

export function HintsProvider({ children }) {
  const { isSubscriber } = usePlatformAuth();
  const [seen, setSeen] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false); // suppress hints while an overlay is up

  useEffect(() => {
    if (!isSubscriber) { setSeen(new Set()); setLoaded(false); posted.current = new Set(); return; }
    let alive = true;
    platformClient.get("/users/me/hints")
      .then(({ data }) => { if (alive) { setSeen(new Set(data.seen || [])); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [isSubscriber]);

  const posted = useRef(new Set());
  const markSeen = useCallback((key) => {
    setSeen((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (posted.current.has(key)) return;
    posted.current.add(key);
    platformClient.post(`/users/me/hints/${key}/seen`, {}).catch(() => {});
  }, []);

  const shouldShow = useCallback(
    (key) => loaded && isSubscriber && !busy && !seen.has(key),
    [loaded, isSubscriber, busy, seen],
  );

  return (
    <HintsContext.Provider value={{ shouldShow, markSeen, setBusy, loaded, busy }}>
      {children}
    </HintsContext.Provider>
  );
}

export const useHints = () => useContext(HintsContext);
